import React, { useState, useEffect } from 'react';
import { usePharmacyStore } from '../../../stores/pharmacyStore';
import { humanizeAttachmentLabel } from '../../../utils/attachmentLabel';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Slider,
  Stack,
  Chip,
  Tabs,
  Tab,
  LinearProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Fullscreen as FullscreenIcon,
  RotateLeft as RotateLeftIcon,
  RotateRight as RotateRightIcon,
  Print as PrintIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  Description as ScriptIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { RxImage, RxPrescription, rxService } from '../../../services/rxService';
import { ImageConverter } from './utils/imageConverter';
import { useImagePan } from '../../common/useImagePan';
import { format } from 'date-fns';
import { headerStripSurface, panelNeutral } from '../../../theme/gridSurfaces';

interface RxImageModalProps {
  open: boolean;
  onClose: () => void;
  prescription: RxPrescription | null;
  prescriptions?: RxPrescription[];
  image: RxImage | null;
  images?: RxImage[];
  loading: boolean;
  loadingProgress?: { current: number; total: number };
  error: string | null;
  onDownload: () => void;
}

export const RxImageModal: React.FC<RxImageModalProps> = ({
  open,
  onClose,
  prescription,
  prescriptions = [],
  image,
  images = [],
  loading,
  loadingProgress,
  error,
  onDownload,
}) => {
  // PRX-30: label Fillware attachments from their scan-time comment.
  const labelsFromComments = usePharmacyStore((s) => s.activePharmacy?.databaseType === 'fillware');
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [tiffUrls, setTiffUrls] = useState<string[]>([]);
  const [currentTiffPage, setCurrentTiffPage] = useState(0);
  const [currentTab, setCurrentTab] = useState(0);

  // Grab-to-pan + scroll for the zoomed image (see useImagePan).
  const pan = useImagePan();

  const hasMultipleImages = images.length > 1;
  const currentImage = hasMultipleImages ? images[currentTab] : image;
  const currentPrescription = hasMultipleImages && prescriptions.length > currentTab
    ? prescriptions[currentTab]
    : prescription;
  const isPdf = (currentImage?.mimeType || '').toLowerCase() === 'application/pdf';

  useEffect(() => {
    if (currentImage && currentImage.content) {
      prepareImage();
    }
  }, [currentImage, currentTab]);

  // Revoke the *previous* blob URL whenever it's replaced (and on unmount).
  // React runs an effect's cleanup with the closure values from when the
  // effect last ran, so depending on the URLs here means each blob is freed
  // exactly when a new one takes its place — critical for multi-MB PDF blobs
  // when a user tabs through multiple prescriptions.
  useEffect(() => {
    return () => {
      if (imageUrl && imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  useEffect(() => {
    return () => {
      tiffUrls.forEach(url => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [tiffUrls]);

  // Re-measure pan availability after any layout-affecting view change.
  useEffect(() => {
    pan.refresh();
  }, [zoom, rotation, imageUrl, currentTiffPage, pan.refresh]);

  const prepareImage = async () => {
    if (!currentImage) return;

    try {
      console.log('RxImageModal - Preparing image:', {
        mimeType: currentImage.mimeType,
        compressionType: currentImage.compressionType,
        contentType: typeof currentImage.content,
        contentLength: currentImage.content?.length
      });

      const prepared = await ImageConverter.prepareImageForDisplay(
        currentImage.content,
        currentImage.mimeType,
        currentImage.compressionType
      );

      console.log('RxImageModal - Image prepared:', {
        hasBlobUrl: !!prepared.blobUrl,
        hasDataUrl: !!prepared.dataUrl,
        hasTiffUrls: !!prepared.tiffUrls,
        tiffCount: prepared.tiffUrls?.length || 0
      });

      // If it's a TIFF with multiple pages, use the tiff URLs
      if (prepared.tiffUrls && prepared.tiffUrls.length > 0) {
        setTiffUrls(prepared.tiffUrls);
        setImageUrl(prepared.tiffUrls[0]); // Set first page as default
        setCurrentTiffPage(0);
        console.log('Using TIFF URLs, count:', prepared.tiffUrls.length);
      } else {
        // PDFs render in an <iframe>; blob URLs are far more reliable than
        // multi-MB data URLs for PDF rendering across browsers.
        const preferBlob = (currentImage.mimeType || '').toLowerCase() === 'application/pdf';
        const urlToUse = preferBlob
          ? (prepared.blobUrl || prepared.dataUrl)
          : (prepared.dataUrl || prepared.blobUrl);
        setImageUrl(urlToUse);
        setTiffUrls([]);
        console.log('Using regular image URL, type:', urlToUse.startsWith('data:') ? 'data URL' : 'blob URL');
      }
    } catch (error) {
      console.error('Failed to prepare image:', error);
      setImageUrl(null);
      setTiffUrls([]);
    }
  };

  const handleZoomChange = (event: Event, value: number | number[]) => {
    setZoom(value as number);
  };

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 25, 300));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 25, 25));
  };

  const handleRotateLeft = () => {
    setRotation((prev) => prev - 90);
  };

  const handleRotateRight = () => {
    setRotation((prev) => prev + 90);
  };

  const handleFullscreen = () => {
    if (imageUrl) {
      window.open(imageUrl, '_blank');
    }
  };

  const handlePrint = () => {
    if (imageUrl) {
      const printWindow = window.open(imageUrl, '_blank');
      if (printWindow) {
        printWindow.addEventListener('load', () => {
          printWindow.print();
        });
      }
    }
  };

  const resetView = () => {
    setZoom(100);
    setRotation(0);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    resetView();
  };

  const getImageTypeLabel = (prescription: RxPrescription, index: number) => {
    if (prescription.scriptImageType === 1) return 'Script Image';
    if (prescription.scriptImageType === 2) {
      // Count how many hard copies come before this one
      const hardCopyNumber = prescriptions
        .slice(0, index)
        .filter(p => p.scriptImageType === 2).length + 1;
      return prescriptions.filter(p => p.scriptImageType === 2).length > 1
        ? `Hard Copy ${hardCopyNumber}`
        : 'Hard Copy';
    }
    return 'Document';
  };

  const getImageTypeIcon = (scriptImageType: number) => {
    if (scriptImageType === 1) return <ScriptIcon fontSize="small" />;
    if (scriptImageType === 2) return <CopyIcon fontSize="small" />;
    return null;
  };

  const formatTabDate = (dateString: string | null) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'MM/dd');
    } catch {
      return '';
    }
  };

  const handleTiffPageChange = (page: number) => {
    if (tiffUrls.length > 0 && page >= 0 && page < tiffUrls.length) {
      setCurrentTiffPage(page);
      setImageUrl(tiffUrls[page]);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: '90vw',
          maxWidth: '1400px',
          height: '90vh',
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box flex={1}>
            <Typography variant="h6">Prescription Image Viewer</Typography>
            {currentPrescription && (
              <Box display="flex" gap={1} alignItems="center" mt={1}>
                <Typography variant="body2" color="textSecondary">
                  Rx #{currentPrescription.rxNumber}
                </Typography>
                {currentPrescription.drugName && (
                  <>
                    <Typography variant="body2" color="textSecondary">•</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {currentPrescription.drugName} {currentPrescription.drugStrength}
                    </Typography>
                  </>
                )}
                {currentImage && (
                  <Chip
                    label={ImageConverter.getFileExtension(currentImage.mimeType).toUpperCase().replace('.', '')}
                    size="small"
                    color="primary"
                  />
                )}
              </Box>
            )}
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        {hasMultipleImages && !loading && (
          <Box sx={{ borderBottom: 2, borderColor: 'divider', mt: 2, bgcolor: headerStripSurface }}>
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': {
                  minHeight: 64,
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  mx: 0.5,
                  color: 'text.secondary',
                  '&.Mui-selected': {
                    color: 'primary.main',
                    fontWeight: 600,
                  },
                },
                '& .MuiTabs-indicator': {
                  height: 3,
                  backgroundColor: 'primary.main',
                },
              }}
            >
              {prescriptions.map((presc, index) => (
                <Tab
                  key={index}
                  label={
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        {getImageTypeIcon(presc.scriptImageType || 0)}
                        <Chip
                          label={
                            (labelsFromComments && humanizeAttachmentLabel(presc.description || presc.comment)) ||
                            getImageTypeLabel(presc, index)
                          }
                          size="small"
                          color={presc.scriptImageType === 1 ? 'primary' : 'default'}
                          variant={currentTab === index ? 'filled' : 'outlined'}
                          sx={{
                            fontWeight: currentTab === index ? 600 : 500,
                            fontSize: '0.85rem',
                          }}
                        />
                      </Box>
                      {presc.scanDate && (
                        <Typography
                          variant="caption"
                          color={currentTab === index ? 'primary' : 'textSecondary'}
                          sx={{ fontWeight: currentTab === index ? 500 : 400 }}
                        >
                          {formatTabDate(presc.scanDate)}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </Box>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="400px">
            {loadingProgress && loadingProgress.total > 1 ? (
              <>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Loading images ({loadingProgress.current} of {loadingProgress.total})
                </Typography>
                <Box sx={{ width: '60%', mb: 2 }}>
                  <LinearProgress
                    variant="determinate"
                    value={(loadingProgress.current / loadingProgress.total) * 100}
                  />
                </Box>
              </>
            ) : (
              <CircularProgress />
            )}
          </Box>
        )}

        {error && (
          <Box p={2}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {!loading && !error && imageUrl && (
          <>
            {/* Image Controls — hidden for PDFs since the browser's PDF viewer has its own */}
            {!isPdf && (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2" sx={{ minWidth: '50px' }}>
                    Zoom:
                  </Typography>
                  <IconButton onClick={handleZoomOut} size="small">
                    <ZoomOutIcon />
                  </IconButton>
                  <Slider
                    value={zoom}
                    onChange={handleZoomChange}
                    min={25}
                    max={300}
                    step={25}
                    sx={{ maxWidth: 200 }}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => `${value}%`}
                  />
                  <IconButton onClick={handleZoomIn} size="small">
                    <ZoomInIcon />
                  </IconButton>
                  <Typography variant="body2" sx={{ minWidth: '50px' }}>
                    {zoom}%
                  </Typography>

                  <Box sx={{ flexGrow: 1 }} />

                  <IconButton onClick={handleRotateLeft} size="small" title="Rotate Left">
                    <RotateLeftIcon />
                  </IconButton>
                  <IconButton onClick={handleRotateRight} size="small" title="Rotate Right">
                    <RotateRightIcon />
                  </IconButton>
                  <IconButton onClick={handleFullscreen} size="small" title="Fullscreen">
                    <FullscreenIcon />
                  </IconButton>
                  <IconButton onClick={handlePrint} size="small" title="Print">
                    <PrintIcon />
                  </IconButton>
                  <Button onClick={resetView} size="small" variant="outlined">
                    Reset View
                  </Button>
                </Stack>
              </Box>
            )}

            {/* TIFF Page Navigation (if multiple pages) */}
            {tiffUrls.length > 1 && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
                <IconButton
                  onClick={() => handleTiffPageChange(currentTiffPage - 1)}
                  disabled={currentTiffPage === 0}
                  size="small"
                >
                  <PrevIcon />
                </IconButton>
                <Typography variant="body2">
                  Page {currentTiffPage + 1} of {tiffUrls.length}
                </Typography>
                <IconButton
                  onClick={() => handleTiffPageChange(currentTiffPage + 1)}
                  disabled={currentTiffPage === tiffUrls.length - 1}
                  size="small"
                >
                  <NextIcon />
                </IconButton>
              </Box>
            )}

            {/* Image Display — scroll + grab-to-pan. Sizing with `width` (not
                `transform: scale`) grows the scrollable area so a zoomed image
                can actually be scrolled/panned; `margin: auto` centers it while
                it fits and collapses to start-align on overflow so the full
                image (including the left/top edge) stays reachable. */}
            <Box
              ref={isPdf ? undefined : pan.containerRef}
              onMouseDown={isPdf ? undefined : pan.onMouseDown}
              sx={{
                overflow: 'auto',
                height: tiffUrls.length > 1 ? 'calc(100% - 150px)' : 'calc(100% - 100px)',
                display: 'flex',
                bgcolor: panelNeutral,
                cursor: isPdf ? 'default' : pan.cursor,
                userSelect: pan.isPanning ? 'none' : undefined,
              }}
            >
              {isPdf ? (
                <iframe
                  src={imageUrl}
                  title="Prescription PDF"
                  style={{
                    width: '100%',
                    height: '100%',
                    minHeight: '500px',
                    border: 'none',
                    backgroundColor: 'white',
                  }}
                />
              ) : ImageConverter.isSupportedImageType(currentImage?.mimeType || '') ? (
                <img
                  src={imageUrl}
                  alt="Prescription"
                  draggable={false}
                  onLoad={pan.refresh}
                  onDragStart={(e) => e.preventDefault()}
                  style={{
                    display: 'block',
                    width: `${zoom}%`,
                    maxWidth: 'none',
                    height: 'auto',
                    flexShrink: 0,
                    objectFit: 'contain',
                    transform: `rotate(${rotation}deg)`,
                    transformOrigin: 'center center',
                    transition: pan.isPanning ? 'none' : 'transform 0.3s ease, width 0.2s ease',
                    margin: 'auto',
                    userSelect: 'none',
                  }}
                />
              ) : (
                <Box textAlign="center" p={4}>
                  <Alert severity="warning">
                    This image format ({currentImage?.mimeType}) cannot be displayed directly.
                    Please download the file to view it with an appropriate application.
                  </Alert>
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={onDownload}
                    sx={{ mt: 2 }}
                  >
                    Download File
                  </Button>
                </Box>
              )}
            </Box>

            {/* Image Info */}
            {currentImage && (
              <Box sx={{ mt: 2, p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                <Stack direction="row" spacing={2} justifyContent="center">
                  <Typography variant="caption" color="textSecondary">
                    Scan Date: {format(new Date(currentImage.scanDate), 'MM/dd/yyyy')}
                  </Typography>
                  {(currentImage.pageCount > 1 || tiffUrls.length > 1) && (
                    <Typography variant="caption" color="textSecondary">
                      Total Pages: {tiffUrls.length > 1 ? tiffUrls.length : currentImage.pageCount}
                    </Typography>
                  )}
                  {currentImage.description && (
                    <Typography variant="caption" color="textSecondary">
                      {currentImage.description}
                    </Typography>
                  )}
                </Stack>
              </Box>
            )}
          </>
        )}

        {!loading && !error && !imageUrl && (
          <Box p={4} textAlign="center">
            <Alert severity="info">No image available to display</Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={onDownload}
          disabled={!currentImage || !currentImage.content}
        >
          Download {hasMultipleImages ? 'Current' : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
};